-- | Canonical operation store with explicit indexes.
-- |
-- | Terminal operations are retained indefinitely during this milestone. That
-- | is the current explicit retention policy until the app runtime starts
-- | pruning entries based on a reviewed policy and tests.
module App.Operation.Store
    ( OperationStore
    , emptyOperationStore
    , reserveOperationId
    , recordStartedOperation
    , startOperation
    , completeOperation
    , markSuperseded
    , lookupOperation
    , lookupLatestInScope
    , isOperationActive
    , isScopeActive
    , listGenerationOperations
    , listOperations
    ) where

import Prelude

import App.Operation.Types (Generation, OperationEntry, OperationId(..), OperationKind, OperationScope, OperationStatus(..), operationScopes)
import Data.Array as Arr
import Data.Either (Either, either)
import Data.Map (Map)
import Data.Map as Map
import Data.Maybe (Maybe(..))
import Data.Set (Set)
import Data.Set as Set
import Data.Tuple (Tuple(..))
import Error (ErrorWrapper)

type OperationStore =
    { nextId :: Int
    , entries :: Map OperationId OperationEntry
    , latestByScope :: Map OperationScope OperationId
    , activeByScope :: Map OperationScope (Set OperationId)
    , byGeneration :: Map Generation (Set OperationId)
    , recentTerminal :: Array OperationId
    }

emptyOperationStore :: OperationStore
emptyOperationStore =
    { nextId: 0
    , entries: Map.empty
    , latestByScope: Map.empty
    , activeByScope: Map.empty
    , byGeneration: Map.empty
    , recentTerminal: []
    }

reserveOperationId :: OperationStore -> Tuple OperationId OperationStore
reserveOperationId store =
    let opId = nextOperationId store
    in Tuple opId $ store { nextId = unwrapOperationId opId }

-- | Runtime-side start for an operation id that the pure app update already
-- | reserved and recorded in state before the effect is launched.
recordStartedOperation :: OperationId -> OperationKind -> Maybe Generation -> OperationStore -> OperationStore
recordStartedOperation opId kind generation store = case Map.lookup opId store.entries of
    Just _ ->
        store { nextId = max store.nextId (unwrapOperationId opId) }

    Nothing ->
        let entry =
                { id: opId
                , kind
                , scopes: operationScopes kind generation
                , generation
                , status: OperationRunning
                }
            reserved =
                store
                    { nextId = max store.nextId (unwrapOperationId opId)
                    , entries = Map.insert opId entry store.entries
                    }
        in activateEntry entry reserved

-- | Reserve and start an operation in one pure step. This is useful in tests
-- | and in update paths that do not need the runtime reservation handshake.
startOperation :: OperationKind -> Maybe Generation -> OperationStore -> Tuple OperationId OperationStore
startOperation kind generation store =
    let Tuple opId reserved = reserveOperationId store
    in Tuple opId $ recordStartedOperation opId kind generation reserved

completeOperation :: forall a. OperationId -> Either ErrorWrapper a -> OperationStore -> OperationStore
completeOperation opId result store = case Map.lookup opId store.entries of
    Nothing -> store
    Just entry ->
        let nextEntry = case entry.status of
                OperationSuperseded -> entry
                _ -> entry { status = either OperationFailed (const OperationSucceeded) result }
            baseStore = deactivateEntry entry store
        in baseStore
            { entries = Map.insert opId nextEntry baseStore.entries
            , recentTerminal = noteTerminal opId baseStore.recentTerminal
            }

markSuperseded :: OperationId -> OperationStore -> OperationStore
markSuperseded opId store = case Map.lookup opId store.entries of
    Nothing -> store
    Just entry ->
        let nextEntry = case entry.status of
                OperationSucceeded -> entry
                OperationFailed _ -> entry
                _ -> entry { status = OperationSuperseded }
            baseStore = deactivateEntry entry store
        in baseStore
            { entries = Map.insert opId nextEntry baseStore.entries
            , recentTerminal = noteTerminal opId baseStore.recentTerminal
            }

lookupOperation :: OperationStore -> OperationId -> Maybe OperationEntry
lookupOperation store opId = Map.lookup opId store.entries

lookupLatestInScope :: OperationStore -> OperationScope -> Maybe OperationEntry
lookupLatestInScope store scope = do
    opId <- Map.lookup scope store.latestByScope
    lookupOperation store opId

isOperationActive :: OperationStore -> OperationId -> Boolean
isOperationActive store opId = case lookupOperation store opId of
    Just { status: OperationRunning } -> true
    _ -> false

isScopeActive :: OperationStore -> OperationScope -> Boolean
isScopeActive store scope = case Map.lookup scope store.activeByScope of
    Nothing -> false
    Just ids -> not $ Set.isEmpty ids

listGenerationOperations :: OperationStore -> Generation -> Array OperationEntry
listGenerationOperations store generation = case Map.lookup generation store.byGeneration of
    Nothing -> []
    Just ids -> Arr.catMaybes $ map (lookupOperation store) $ Arr.fromFoldable ids

listOperations :: OperationStore -> Array OperationEntry
listOperations = Arr.fromFoldable <<< Map.values <<< _.entries

nextOperationId :: OperationStore -> OperationId
nextOperationId store = OperationId (store.nextId + 1)

unwrapOperationId :: OperationId -> Int
unwrapOperationId (OperationId i) = i

activateEntry :: OperationEntry -> OperationStore -> OperationStore
activateEntry entry store =
    let withLatest = Arr.foldl (\acc scope -> acc { latestByScope = Map.insert scope entry.id acc.latestByScope }) store entry.scopes
        withActive = Arr.foldl (\acc scope -> acc { activeByScope = addScopeMembership scope entry.id acc.activeByScope }) withLatest entry.scopes
    in case entry.generation of
        Nothing -> withActive
        Just generation ->
            withActive { byGeneration = addGenerationMembership generation entry.id withActive.byGeneration }

deactivateEntry :: OperationEntry -> OperationStore -> OperationStore
deactivateEntry entry store =
    store
        { activeByScope = Arr.foldl (\acc scope -> removeScopeMembership scope entry.id acc) store.activeByScope entry.scopes
        }

addScopeMembership :: OperationScope -> OperationId -> Map OperationScope (Set OperationId) -> Map OperationScope (Set OperationId)
addScopeMembership scope opId =
    Map.alter update scope
  where
    update = case _ of
        Nothing -> Just $ Set.singleton opId
        Just ids -> Just $ Set.insert opId ids

removeScopeMembership :: OperationScope -> OperationId -> Map OperationScope (Set OperationId) -> Map OperationScope (Set OperationId)
removeScopeMembership scope opId =
    Map.update update scope
  where
    update ids =
        let nextIds = Set.delete opId ids
        in if Set.isEmpty nextIds then Nothing else Just nextIds

addGenerationMembership :: Generation -> OperationId -> Map Generation (Set OperationId) -> Map Generation (Set OperationId)
addGenerationMembership generation opId =
    Map.alter update generation
  where
    update = case _ of
        Nothing -> Just $ Set.singleton opId
        Just ids -> Just $ Set.insert opId ids

noteTerminal :: OperationId -> Array OperationId -> Array OperationId
noteTerminal opId ids =
    if Arr.elem opId ids then ids else Arr.snoc ids opId
